import { useEffect, useRef, useState, type RefObject } from 'react';
import * as Sentry from '@sentry/react';
import { MessageSquare } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@/lib/utils';

// Resting spot (bottom-6 right-6) in px, used to probe what lies under it.
const REST_OFFSET_PX = 24;
// Controls the button must not cover, plus the whole pinned form action bar
// (its height is what bottom-24 clears).
const COVER_SELECTOR =
  'button, a[href], input, select, textarea, [role="button"], [role="combobox"], [role="tab"], [data-testid="form-action-bar"][data-pinned="true"]';

/**
 * True while a control (a table's pagination, the pinned form action bar) sits
 * under the button's resting spot, so the button can step up instead of
 * covering it.
 */
function useControlUnderneath(buttonRef: RefObject<HTMLButtonElement | null>, enabled: boolean) {
  const [covered, setCovered] = useState(false);

  useEffect(() => {
    if (!enabled || typeof document.elementsFromPoint !== 'function') return;
    let frame = 0;

    const check = () => {
      frame = 0;
      const button = buttonRef.current;
      if (!button) return;
      const size = button.offsetWidth;
      const right = window.innerWidth - REST_OFFSET_PX;
      const bottom = window.innerHeight - REST_OFFSET_PX;
      // Probe the centre and the left and top edges of the resting spot.
      const points: Array<[number, number]> = [
        [right - size / 2, bottom - size / 2],
        [right - size + 2, bottom - size / 2],
        [right - size / 2, bottom - size + 2],
      ];
      const hit = points.some(([x, y]) =>
        document
          .elementsFromPoint(x, y)
          .some((node) => !button.contains(node) && node.closest(COVER_SELECTOR) !== null),
      );
      setCovered(hit);
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };

    // Capture catches scrolling inside page containers, not only the window.
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    schedule();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, [buttonRef, enabled]);

  return covered;
}

export function FeedbackButton() {
  // Add error boundary for hot reload scenarios
  let isSignedIn = false;
  let theme: "light" | "dark" = 'light';

  try {
    const authContext = useAuthContext();
    isSignedIn = authContext.isSignedIn;
  } catch (error) {
    // During development hot reload, context might be temporarily unavailable
    console.warn('Auth context temporarily unavailable during hot reload:', error);
    isSignedIn = false;
  }

  try {
    const themeContext = useTheme();
    theme = themeContext.theme; // Always resolved to "light" or "dark"
  } catch (error) {
    // During development hot reload, context might be temporarily unavailable
    console.warn('Theme context temporarily unavailable during hot reload:', error);
    theme = 'light';
  }

  const { t } = useLingui();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const [isAttached, setIsAttached] = useState(false);
  const lifted = useControlUnderneath(buttonRef, isSignedIn);

  useEffect(() => {
    if (!isSignedIn || !buttonRef.current) return;

    const feedback = Sentry.getFeedback();
    if (!feedback) {
      console.warn('Sentry feedback integration not available');
      return;
    }

    // Small delay to ensure button is fully rendered
    const attachTimeout = setTimeout(() => {
      if (!buttonRef.current) return;

      // Clean up any existing attachment first
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      // Attach Sentry's feedback handler to our custom button
      const unsubscribe = feedback.attachTo(buttonRef.current, {
        formTitle: t`Send Feedback`,
        colorScheme: theme,
        showBranding: false,
        showName: true,
        showEmail: true,
        isNameRequired: false,
        isEmailRequired: true,
        nameLabel: t`Name`,
        namePlaceholder: t`Your Name`,
        emailLabel: t`Email`,
        emailPlaceholder: t`your.email@example.org`,
        messageLabel: t`Description`,
        messagePlaceholder: t`What's the bug? What did you expect?`,
        isRequiredLabel: t`(required)`,
        submitButtonLabel: t`Send`,
        cancelButtonLabel: t`Cancel`,
        successMessageText: t`Thank you for your feedback!`,
        addScreenshotButtonLabel: t`Add a screenshot`,
        removeScreenshotButtonLabel: t`Remove screenshot`,
        onFormOpen: () => {
          // Focus the description textarea when the form opens
          setTimeout(() => {
            const host = document.getElementById("sentry-feedback");
            if (host?.shadowRoot) {
              const textarea = host.shadowRoot.querySelector("textarea");
              textarea?.focus();
            }
          }, 50);
        },
      });

      // Store the unsubscribe function
      unsubscribeRef.current = unsubscribe;
      setIsAttached(true);
    }, 50);

    // Cleanup function
    return () => {
      clearTimeout(attachTimeout);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
        setIsAttached(false);
      }
    };
  }, [isSignedIn, theme, t]); // Re-attach when sign-in state, theme, or language changes
  
  // Only show button when user is logged in
  if (!isSignedIn) {
    return null;
  }

  return (
    <button
      ref={buttonRef}
      className={cn(
        'fixed right-6 z-50 rounded-full bg-primary p-2.5 text-primary-foreground shadow-lg transition-all hover:bg-primary/90 print:hidden',
        // bottom-24 clears a one-row pinned FormActionBar (73px + 8px inset margin).
        lifted ? 'bottom-24' : 'bottom-6',
      )}
      aria-label={t`Send feedback`}
      type="button"
      data-sentry-feedback-attached={isAttached}
      data-lifted={lifted || undefined}
    >
      <MessageSquare className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}